/**
 * The inbox list view's client-side filters (Responsible, Due) and the rule for
 * which items "Acknowledge selected / all" may close.
 *
 * Responsible is worked out per row at read time (person > group lead > role >
 * nobody, see `responsibleFor`), and Due is a date range the list endpoint does
 * not take. Both therefore filter the complete set of the view on the client,
 * like a Category chip with several values.
 */

import type { I18n } from '@lingui/core';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';

import type { WorkflowItemResponse } from '@/types/workflow';
import {
  responsibleFor,
  roleQueueLabel,
  type Responsible,
  type ResponsibleContext,
} from '@/types/workflowResponsible';

/** A Due filter: either end may be open. */
export interface DueRange {
  from?: Date;
  to?: Date;
}

/**
 * The Responsible filter value of a row. A person who holds items and leads a
 * group is one value, so "Anna" finds both; a lead without a login falls back
 * to the employee id.
 */
export function responsibleKey(r: Responsible): string {
  switch (r.kind) {
    case 'person':
      return `person:${r.clerkUserId}`;
    case 'lead':
      return r.clerkUserId ? `person:${r.clerkUserId}` : `lead:${r.employeeId}`;
    case 'role':
      return `role:${r.role}`;
    case 'nobody':
      return 'nobody';
  }
}

/** The role queues offered even when no loaded row waits for them. */
const ALWAYS_OFFERED_ROLES = ['admin', 'educator'];

/**
 * The Responsible filter options: the role queues and "Nobody yet" first, then
 * every person by name. People come from the employee picker (so a person with
 * no item on the loaded page is still offered) and from the loaded rows (a lead
 * without a login).
 */
export function responsibleFilterOptions(
  i18n: Pick<I18n, '_' | 'locale'>,
  rows: readonly Pick<
    WorkflowItemResponse,
    'assigned_to' | 'assigned_role' | 'admin_only' | 'group_lead_employee_id' | 'group_lead_name' | 'group_lead_clerk_user_id' | 'group_name'
  >[],
  ctx: ResponsibleContext,
): { value: string; label: string }[] {
  const roles = new Set(ALWAYS_OFFERED_ROLES);
  const people = new Map<string, string>();
  for (const [clerkUserId, name] of ctx.namesByClerkUserId) {
    people.set(`person:${clerkUserId}`, name);
  }
  for (const row of rows) {
    const r = responsibleFor(row, ctx);
    if (r.kind === 'role') roles.add(r.role);
    if (r.kind === 'person' || r.kind === 'lead') {
      const key = responsibleKey(r);
      if (!people.has(key)) people.set(key, r.name);
    }
  }
  const you = i18n._({ id: 'workflow.responsible.you', message: 'You' });
  const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, i18n.locale);
  const roleOptions = [...roles].map((role) => ({ value: `role:${role}`, label: roleQueueLabel(i18n, role) })).sort(byLabel);
  const personOptions = [...people]
    .map(([value, name]) => {
      const isMe = !!ctx.userId && value === `person:${ctx.userId}`;
      if (isMe) return { value, label: name ? `${name} (${you})` : you };
      return { value, label: name || ctx.unknownName };
    })
    .sort(byLabel);
  return [
    ...roleOptions,
    { value: 'nobody', label: i18n._({ id: 'workflow.responsible.nobody', message: 'Nobody yet' }) },
    ...personOptions,
  ];
}

/** The date range of a Due filter chip, or `null` when it sets neither end. */
export function chipDateRange(filters: ColumnFiltersState, id: string): DueRange | null {
  const value = filters.find((filter) => filter.id === id)?.value as DueRange | undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const from = value.from instanceof Date && isValid(value.from) ? value.from : undefined;
  const to = value.to instanceof Date && isValid(value.to) ? value.to : undefined;
  return from || to ? { from, to } : null;
}

/**
 * The date the Due column shows for the item: the deadline, or for a dunning
 * item the debtor's oldest overdue invoice.
 */
function dueDateOf(item: Pick<WorkflowItemResponse, 'category' | 'due_at' | 'oldest_overdue_date'>): Date | null {
  const raw = item.category === 'dunning' ? item.oldest_overdue_date : item.due_at;
  if (!raw) return null;
  const date = parseISO(raw);
  return isValid(date) ? date : null;
}

/** Is the item due inside the range, both days included? An item without a date never is. */
export function isDueInRange(
  item: Pick<WorkflowItemResponse, 'category' | 'due_at' | 'oldest_overdue_date'>,
  range: DueRange,
): boolean {
  const due = dueDateOf(item);
  if (!due) return false;
  if (range.from && due < startOfDay(range.from)) return false;
  if (range.to && due > endOfDay(range.to)) return false;
  return true;
}

export interface InboxClientFilters {
  /** Only change_request items (the change-request tab). */
  changeRequestsOnly: boolean;
  categories: readonly string[];
  states: readonly string[];
  responsibleKeys: readonly string[];
  due: DueRange | null;
}

/** Applies the list view's chips to loaded items; an empty chip filters nothing. */
export function applyInboxClientFilters<T extends WorkflowItemResponse>(
  items: readonly T[],
  filters: InboxClientFilters,
  ctx: ResponsibleContext,
): T[] {
  return items.filter(
    (item) =>
      (!filters.changeRequestsOnly || item.category === 'change_request') &&
      (filters.categories.length === 0 || filters.categories.includes(item.category ?? '')) &&
      (filters.states.length === 0 || filters.states.includes(item.current_state ?? '')) &&
      (filters.responsibleKeys.length === 0 ||
        filters.responsibleKeys.includes(responsibleKey(responsibleFor(item, ctx)))) &&
      (!filters.due || isDueInRange(item, filters.due)),
  );
}

/**
 * The categories whose `acknowledge` only takes note of a notice and closes it.
 * Not here: the accident report, where acknowledge is one step of several, and
 * the care alerts, which need a comment per item.
 */
const BULK_ACKNOWLEDGE_CATEGORIES: ReadonlySet<string> = new Set([
  'portfolio_conversation_due',
  'pickup_exception',
  'split_alert',
  'subsidy_expiry',
]);

/** May "Acknowledge selected / all" close this item without a person looking at it? */
export function isBulkAcknowledgeable(
  item: Pick<WorkflowItemResponse, 'category' | 'available_actions' | 'comment_required_actions' | 'completed_at'>,
): boolean {
  return (
    !item.completed_at &&
    BULK_ACKNOWLEDGE_CATEGORIES.has(item.category ?? '') &&
    (item.available_actions ?? []).includes('acknowledge') &&
    !(item.comment_required_actions ?? []).includes('acknowledge')
  );
}
